import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Headphones, Trash2, Pencil, X, Loader2, Link2, Upload } from "lucide-react";

const field = "w-full bg-black/50 border border-white/10 px-4 py-2.5 text-bone focus:outline-none focus:border-gold/50 text-sm";
const lbl = "text-xs uppercase tracking-widest text-bone/60 mb-1.5 block";
const CATS = [
  ["kemetic", "Kemetic"], ["nuwaupian", "Nuwaupian"], ["noi", "Nation of Islam"], ["moorish", "Moorish"],
  ["hebrew", "Hebrew Israelite"], ["metaphysical", "Metaphysical"], ["panafrican", "Pan-African"],
  ["esoteric", "Esoteric"], ["astro", "Astro / Cosmology"], ["herbal", "Herbal / Health"],
];
const blank = { title: "", author: "", narrator: "", category: "panafrican", description: "", external_url: "", cover_image: "", source_label: "", featured: false };

export function AudiobookManager() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("link"); // link | upload
  const [f, setF] = useState(blank);
  const [file, setFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => { setLoading(true); api.get("/audiobooks").then((r) => setBooks(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const reset = () => { setF(blank); setFile(null); setEditingId(null); };
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (editingId) {
        await api.put(`/audiobooks/${editingId}`, f);
        toast.success("Audiobook updated");
      } else if (mode === "upload") {
        if (!file) { toast.error("Choose an audio file"); setBusy(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        ["title", "author", "narrator", "category", "description", "cover_image"].forEach((k) => fd.append(k, f[k] || ""));
        fd.append("featured", f.featured ? "true" : "false");
        await api.post("/audiobooks/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Audiobook uploaded");
      } else {
        await api.post("/audiobooks", f);
        toast.success("Audiobook added");
      }
      reset(); load();
    } catch { toast.error(editingId ? "Update failed" : "Save failed"); }
    finally { setBusy(false); }
  };

  const edit = (b) => {
    setF({ title: b.title || "", author: b.author || "", narrator: b.narrator || "", category: b.category || "panafrican",
      description: b.description || "", external_url: b.external_url || "", cover_image: b.cover_image || "",
      source_label: b.source_label || "", featured: !!b.featured });
    setEditingId(b.id); setMode("link");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (b) => {
    if (!window.confirm(`Delete the audiobook "${b.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/audiobooks/${b.id}`); toast.success("Deleted"); if (editingId === b.id) reset(); load(); }
    catch { toast.error("Delete failed"); }
  };

  const uploadMode = mode === "upload" && !editingId;

  return (
    <div data-testid="admin-audiobooks">
      <h2 className="font-serif text-2xl text-bone mb-2 flex items-center gap-2"><Headphones size={22} className="text-gold" /> Audiobook Library</h2>
      <p className="text-bone/50 text-sm mb-6">Add audiobooks by pasting a link (LibriVox, Archive.org, YouTube, Spotify, Audible) or by uploading an audio file. Edit, feature or remove any below.</p>

      <form onSubmit={submit} className="bg-surface border border-white/10 p-8 space-y-5 max-w-2xl mb-10" data-testid="admin-audiobook-form">
        {editingId ? (
          <div className="flex items-center justify-between bg-gold/10 border border-gold/30 px-4 py-2 text-sm text-gold">
            <span>Editing existing audiobook</span>
            <button type="button" onClick={reset} className="flex items-center gap-1 hover:text-bone" data-testid="admin-audiobook-cancel"><X size={14} /> Cancel</button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button type="button" onClick={() => setMode("link")} data-testid="admin-audiobook-mode-link"
              className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm border transition-colors ${mode === "link" ? "bg-gold text-black border-gold" : "border-white/15 text-bone/70 hover:border-gold/40"}`}><Link2 size={15} /> Paste Link</button>
            <button type="button" onClick={() => setMode("upload")} data-testid="admin-audiobook-mode-upload"
              className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm border transition-colors ${mode === "upload" ? "bg-gold text-black border-gold" : "border-white/15 text-bone/70 hover:border-gold/40"}`}><Upload size={15} /> Upload File</button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div><label className={lbl}>Title</label><input required value={f.title} onChange={(e) => set("title", e.target.value)} className={field} data-testid="admin-audiobook-title" /></div>
          <div><label className={lbl}>Author</label><input value={f.author} onChange={(e) => set("author", e.target.value)} className={field} data-testid="admin-audiobook-author" /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={lbl}>Narrator</label><input value={f.narrator} onChange={(e) => set("narrator", e.target.value)} className={field} data-testid="admin-audiobook-narrator" /></div>
          <div><label className={lbl}>Category</label>
            <select value={f.category} onChange={(e) => set("category", e.target.value)} className={field} data-testid="admin-audiobook-category">
              {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>

        {uploadMode ? (
          <div><label className={lbl}>Audio File (MP3, M4A, etc.)</label>
            <input type="file" accept="audio/*" onChange={(e) => setFile(e.target.files[0])} className={field} data-testid="admin-audiobook-file" /></div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Source URL (link / YouTube)</label><input value={f.external_url} onChange={(e) => set("external_url", e.target.value)} className={field} placeholder="https://librivox.org/..." data-testid="admin-audiobook-url" /></div>
            <div><label className={lbl}>Source Label</label><input value={f.source_label} onChange={(e) => set("source_label", e.target.value)} className={field} placeholder="LibriVox, Spotify..." data-testid="admin-audiobook-source" /></div>
          </div>
        )}

        <div><label className={lbl}>Cover Image URL (optional)</label><input value={f.cover_image} onChange={(e) => set("cover_image", e.target.value)} className={field} data-testid="admin-audiobook-cover" /></div>
        <div><label className={lbl}>Description</label><textarea value={f.description} onChange={(e) => set("description", e.target.value)} className={field} rows={3} data-testid="admin-audiobook-description" /></div>
        <label className="flex items-center gap-2 text-sm text-bone/70 cursor-pointer">
          <input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="admin-audiobook-featured" /> Feature on the top shelf
        </label>

        <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-gold text-black px-6 py-2.5 text-sm font-medium hover:bg-gold/90 disabled:opacity-50 transition-colors" data-testid="admin-audiobook-submit">
          {busy && <Loader2 size={16} className="animate-spin" />} {editingId ? "Save Changes" : uploadMode ? "Upload Audiobook" : "Add Audiobook"}
        </button>
      </form>

      <h3 className="font-serif text-xl text-bone mb-3">Library ({books.length})</h3>
      {loading ? (
        <div className="text-bone/50 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading...</div>
      ) : books.length === 0 ? (
        <p className="text-bone/50 text-sm" data-testid="admin-audiobook-empty">No audiobooks yet.</p>
      ) : (
        <div className="border border-white/10 divide-y divide-white/10">
          {books.map((b) => (
            <div key={b.id} className="flex items-center gap-3 p-3 bg-surface text-sm" data-testid="admin-audiobook-row">
              {b.featured && <span className="text-[10px] uppercase tracking-widest text-black bg-gold px-2 py-0.5">Featured</span>}
              <div className="flex-1 min-w-0">
                <p className="text-bone truncate">{b.title}</p>
                <p className="text-bone/40 text-xs truncate">{b.author} · {b.category} · {b.source_label || b.source_type}{b.plays ? ` · ${b.plays} plays` : ""}</p>
              </div>
              <button onClick={() => edit(b)} className="text-bone/50 hover:text-gold" data-testid="admin-audiobook-edit"><Pencil size={15} /></button>
              <button onClick={() => del(b)} className="text-bone/50 hover:text-red-400" data-testid="admin-audiobook-delete"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
